import { FC, Fragment, MouseEvent } from 'react';

// Third-party
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';

// Utils
import { useTheme } from '@mui/material';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { TCustomTheme } from '@mgm/common/theme';
import { useCustomTheme } from '@mgm/common/hooks/useCustomTheme';
import { Iconify } from '@mgm/common/components';
import { dateFormatTZ } from '@mgm/common/utils';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';

// Redux-toolkit
import { schedulesActions } from '../../../../store/schedules/slice';

// Types
import { TScheduleShifts } from '../type';
import { TScheduleShift } from '../../../../types';
import { getMembers } from '../../../../store/team/selectors';

const ScheduleShifts: FC<TScheduleShifts> = ({ memberShifts, setAnchorEl }) => {
  const dispatch = useAppDispatch();
  const members = useAppSelector(getMembers);

  const theme = useTheme() as TCustomTheme;

  const { isLight } = useCustomTheme(theme);

  const handleShift = (event: MouseEvent<HTMLElement>, shift: TScheduleShift) => {
    setAnchorEl(event.currentTarget);
    dispatch(schedulesActions.setSelectedScheduleShift(shift));
  };

  const onNoteClicked = (event: MouseEvent<HTMLElement>, shift: TScheduleShift) => {
    event.stopPropagation();
    dispatch(schedulesActions.setSelectedScheduleShift(shift));

    dispatch(schedulesActions.setIsOpenShiftNotesNav(true));
  };

  const getShiftColorByPaymentType = (email: string) => {
    const selectedMember = members.find((member) => member.email === email);

    return selectedMember?.payment?.paymentType === 'Salary' ? {
      bg: '#7954A1',
      titleColor: '#F4F6F8',
      subtitleColor: '#DFE3E8',
    } : {
      bg: 'rgba(168, 85, 247, 0.16)',
      subtitleColor: '#637381',
      ...(!isLight && {
        subtitleColor: '#DFE3E8',
      }),
    };
  };

  return (
    <List sx={{ width: '100%', px: 1, my: 0.5 }}>
      {memberShifts.map((shift, index) => (
        <Fragment key={shift.id}>
          <ListItem
            className="cursor-pointer"
            onClick={(e) => handleShift(e, shift)}
            sx={{
              bgcolor: getShiftColorByPaymentType(shift.email).bg,
              paddingY: 0.5,
              marginTop: index > 0 ? 0.7 : 0,
            }}
          >
            <ListItemText
              primaryTypographyProps={{
                fontSize: '11px !important',
                ...(getShiftColorByPaymentType(shift.email).titleColor && {
                  color: getShiftColorByPaymentType(shift.email).titleColor,
                }),
              }}
              secondaryTypographyProps={{
                fontSize: '11px !important',
                ...(getShiftColorByPaymentType(shift.email).subtitleColor && {
                  color: getShiftColorByPaymentType(shift.email).subtitleColor,
                }),
              }}
              primary={(
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography fontSize={11}>
                    {dateFormatTZ(shift.start, 'h:mm A', shift.timezone)}
                    {' '}
                    {dateFormatTZ(shift.end, 'h:mm A', shift.timezone)}
                  </Typography>
                  {shift.notes?.length > 0 && (
                  <Box
                    sx={{
                      cursor: 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                      '&:active': {
                        backgroundColor: 'transparent',
                      },
                    }}
                    onClick={(e) => onNoteClicked(e, shift)}
                  >
                    <Iconify width={16} icon="proicons:note" />
                  </Box>
                  )}
                </Stack>
              )}
              secondary={`${shift?.position || 'N/A'} - ${shift.department}`}
            />
          </ListItem>
          {index !== memberShifts.length - 1 && (
          <Divider sx={{
            borderStyle: 'dashed',
          }}
          />
          )}
        </Fragment>
      ))}
    </List>
  );
};

export {
  ScheduleShifts,
};
